import React from 'react'
import { useIntl } from 'react-intl'
import { Link } from 'react-router-dom'
import { Typography, Tooltip } from '@mui/material'
import { withStyles } from '@mui/styles'

import { useWidth } from '../../../../core/utils'
import { publicLayoutStyles } from './public-layout.styles'
import { FOOTER_LINKS, CONTACT_PHONE, DEFAULT_ROUTE, APP_VERSION_MSSG } from '../constants'
import { ReactComponent as LogoNedgiaFull } from '../../../../resources/images/logo_nedgia_full.svg'
import { ReactComponent as LogoNedgia } from '../../../../resources/images/logo_nedgia.svg'

/**
 * @typedef {object} PublicLayoutComponentProps
 * @property {React.ReactNode} children - El contenido principal de la página que se renderizará en el área del cuerpo.
 * @property {object} classes - Objeto con los nombres de las clases CSS inyectado por `withStyles`.
 */

/**
 * Componente de layout para páginas públicas (no autenticadas) de la aplicación.
 * 
 * Proporciona la estructura completa de la interfaz para usuarios no autenticados, incluyendo:
 * - **Header con logo**: Logo de Nedgia responsive con tooltip de versión de la aplicación
 * - **Información de contacto**: Teléfono de atención al cliente en el header
 * - **Footer con enlaces**: Enlaces a políticas, términos y condiciones
 * - **Layout responsive**: Adaptación automática a diferentes tamaños de pantalla
 * - **Internacionalización**: Todos los textos traducidos mediante react-intl
 * 
 * El componente muestra diferentes versiones del logo según el tamaño de pantalla
 * y organiza los enlaces del footer de forma dinámica.
 * 
 * @component
 * @example
 * const LoginPage = () => {
 *   return (
 *     <PublicLayoutComponent>
 *       <div>
 *         <h1>Iniciar Sesión</h1>
 *         <LoginForm />
 *       </div>
 *     </PublicLayoutComponent>
 *   );
 * };
 * 
 * @param {PublicLayoutComponentProps} props - Las props del componente PublicLayoutComponent.
 */
export const PublicLayoutComponent = withStyles(publicLayoutStyles)(({ children, classes }) => {
  let { formatMessage } = useIntl()
  let screenWidth = useWidth()
  const isMobileWidth = screenWidth === 'xs'

  return (
    <div className={classes.root}>
      <div className={classes.headerWrapper}>
        <div className={classes.headerLeftContent}>
          <Tooltip title={APP_VERSION_MSSG}>
            <Link to={DEFAULT_ROUTE}>{isMobileWidth ? <LogoNedgia /> : <LogoNedgiaFull />}</Link>
          </Tooltip>
        </div>
        <div className={classes.headerRightContent}>
          <Typography variant="h6">
            {formatMessage({ id: 'layout.public.header.right.upper.label' })}
          </Typography>
          <Typography variant="body1">
            {formatMessage(
              { id: 'layout.public.header.right.bottom.label' },
              { phone: CONTACT_PHONE }
            )}
          </Typography>
        </div>
      </div>
      <div className={classes.bodyWrapper}>{children}</div>
      <div className={classes.footerWrapper}>
        <div className={classes.footerContent}>
          <Typography variant="body1" component="p" marginBottom="1rem">
            {FOOTER_LINKS.map((item, i) => (
              <span key={i}>
                <span className={classes.footerLink} onClick={() => window.open(item.link)}>
                  {formatMessage({ id: item.id })}
                </span>
                {i !== FOOTER_LINKS.length - 1 && '|'}
              </span>
            ))}
          </Typography>
          <Typography variant="body1">
            {formatMessage({ id: 'layout.public.footer.bottom.label' })}
          </Typography>
        </div>
      </div>
    </div>
  )
})
